// Deoeditz Effects & Animations registry.
// Every entry is a real WebGL shader body (or explicitly marked as needing
// server-side processing). Adding a new effect = adding one object here.

export const CATEGORIES = [
    { id: "blur", label: "Blur" },
    { id: "glitch", label: "Glitch" },
    { id: "glow", label: "Glow & Light" },
    { id: "color", label: "Color" },
    { id: "sharpen", label: "Sharpen" },
    { id: "motion", label: "Motion" },
    { id: "distort", label: "Distortion" },
    { id: "transitions", label: "Transitions" },
    { id: "creative", label: "Creative" },
];

export const EFFECTS = [
    // ---------- BLUR ----------
    {
        id: "radial_blur",
        name: "Radial Blur",
        cat: "blur",
        controls: ["amount"],
        amountLabel: "Blur amount",
        amountDefault: 0.5,
        body: `vec2 cc = vec2(0.5);
vec3 col = vec3(0.0);
float a = 0.05 * u_amount;
for (int k = 0; k < 10; k++) {
  vec2 uv2 = mix(uv, cc, a * (float(k) / 9.0));
  col += S(uv2);
}
c = col / 10.0;`,
    },
    {
        id: "motion_blur",
        name: "Motion Blur",
        cat: "blur",
        controls: ["amount"],
        amountLabel: "Blur amount",
        amountDefault: 0.5,
        body: `vec3 col = vec3(0.0);
float a = 0.06 * u_amount;
for (int k = 0; k < 9; k++) {
  col += S(uv + vec2(a * (float(k) / 8.0 - 0.5), 0.0));
}
c = col / 9.0;`,
    },
    {
        id: "zoom_blur",
        name: "Zoom Blur",
        cat: "blur",
        controls: ["amount"],
        amountLabel: "Blur amount",
        amountDefault: 0.5,
        body: `vec3 col = vec3(0.0);
float a = 0.07 * u_amount;
for (int k = 0; k < 10; k++) {
  vec2 uv2 = uv + (uv - vec2(0.5)) * (a * (float(k) / 9.0));
  col += S(uv2);
}
c = col / 10.0;`,
    },
    {
        id: "soft_blur",
        name: "Soft Blur",
        cat: "blur",
        controls: ["amount"],
        amountLabel: "Blur amount",
        amountDefault: 0.5,
        body: `float o = 0.003 + 0.012 * u_amount;
vec3 col = S(uv) * 4.0;
col += S(uv + vec2(o, 0.0)) + S(uv - vec2(o, 0.0)) + S(uv + vec2(0.0, o)) + S(uv - vec2(0.0, o));
col += S(uv + vec2(o, o)) + S(uv - vec2(o, o)) + S(uv + vec2(o, -o)) + S(uv - vec2(-o, o));
c = col / 12.0;`,
    },
    {
        id: "directional_blur",
        name: "Directional Blur",
        cat: "blur",
        controls: ["amount"],
        amountLabel: "Blur amount",
        amountDefault: 0.5,
        body: `vec3 col = vec3(0.0);
float a = 0.05 * u_amount;
for (int k = 0; k < 9; k++) {
  col += S(uv + vec2(1.0, 0.62) * a * (float(k) / 8.0 - 0.5));
}
c = col / 9.0;`,
    },

    // ---------- GLITCH ----------
    {
        id: "shaky_glitch",
        name: "Shaky Glitch",
        cat: "glitch",
        controls: ["amount", "speed"],
        amountLabel: "Jitter amount",
        amountDefault: 0.5,
        body: `float t = floor(u_time * 9.0);
float row = floor(uv.y * 24.0);
float on = step(0.55, hash(vec2(t, row)));
float n = hash(vec2(row, t * 7.0));
c = S(uv + vec2((n - 0.5) * 0.07 * u_amount * on, 0.0));`,
    },
    {
        id: "rgb_glitch",
        name: "RGB Glitch",
        cat: "glitch",
        controls: ["amount", "speed"],
        amountLabel: "Split amount",
        amountDefault: 0.5,
        body: `float t = floor(u_time * 8.0 * u_speed);
float band = floor(uv.y * 12.0);
float n = hash(vec2(t, band));
float shift = (n > 0.75 ? (n - 0.75) * 4.0 : 0.0) * 0.09 * u_amount;
c = vec3(S(uv + vec2(shift, 0.0)).r, S(uv).g, S(uv - vec2(shift, 0.0)).b);`,
    },
    {
        id: "chromatic_glitch",
        name: "Chromatic Glitch",
        cat: "glitch",
        controls: ["amount"],
        amountLabel: "Split amount",
        amountDefault: 0.5,
        body: `vec2 d = (uv - vec2(0.5)) * 0.025 * u_amount;
c = vec3(S(uv + d).r, S(uv).g, S(uv - d).b);`,
    },
    {
        id: "digital_glitch",
        name: "Digital Glitch",
        cat: "glitch",
        controls: ["amount", "speed"],
        amountLabel: "Glitch amount",
        amountDefault: 0.5,
        body: `float t = floor(u_time * 6.0 * u_speed);
vec2 block = floor(uv * vec2(16.0, 9.0));
float n = hash(block + t);
vec2 off = vec2((n - 0.5) * 0.12 * u_amount * step(0.78, n), 0.0);
c = S(uv + off);`,
    },
    {
        id: "pixel_glitch",
        name: "Pixel Glitch",
        cat: "glitch",
        controls: ["amount", "speed"],
        amountLabel: "Glitch amount",
        amountDefault: 0.5,
        body: `float px = mix(70.0, 14.0, u_amount);
vec2 p = floor(uv * px) / px;
float t = floor(u_time * 10.0 * u_speed);
float n = hash(vec2(floor(p.y * px), t));
c = S(p + vec2((n - 0.5) * 0.06 * step(0.82, n), 0.0));`,
    },

    // ---------- GLOW & LIGHT ----------
    {
        id: "dreamy_glow",
        name: "Dreamy Glow",
        cat: "glow",
        controls: ["amount"],
        amountLabel: "Glow amount",
        amountDefault: 0.6,
        body: `float o = 0.006 + 0.012 * u_amount;
vec3 blurc = S(uv + vec2(o, 0.0)) + S(uv - vec2(o, 0.0)) + S(uv + vec2(0.0, o)) + S(uv - vec2(0.0, o));
blurc += S(uv + vec2(o, o)) + S(uv - vec2(o, o)) + S(uv + vec2(o, -o)) + S(uv - vec2(-o, o));
blurc /= 8.0;
vec3 bright = max(blurc - 0.3, vec3(0.0)) * 1.5;
vec3 orig = S(uv);
c = 1.0 - (1.0 - orig) * (1.0 - clamp(bright * u_amount * 1.4, 0.0, 1.0));`,
    },
    {
        id: "neon_glow",
        name: "Neon Glow",
        cat: "glow",
        controls: ["amount"],
        amountLabel: "Glow amount",
        amountDefault: 0.5,
        body: `float o = 0.003 + 0.004 * u_amount;
float lx = lum(S(uv + vec2(o, 0.0))) - lum(S(uv - vec2(o, 0.0)));
float ly = lum(S(uv + vec2(0.0, o))) - lum(S(uv - vec2(0.0, o)));
float e = clamp(length(vec2(lx, ly)) * 9.0, 0.0, 1.0);
vec3 glowCol = mix(vec3(1.0, 0.45, 0.1), vec3(0.2, 0.9, 1.0), uv.x) * e;
vec3 orig = S(uv);
c = 1.0 - (1.0 - orig) * (1.0 - clamp(glowCol * u_amount * 1.5, 0.0, 1.0));`,
    },
    {
        id: "lens_glow",
        name: "Lens Glow",
        cat: "glow",
        controls: ["amount"],
        amountLabel: "Glow amount",
        amountDefault: 0.6,
        body: `float d = distance(uv, vec2(0.5));
vec3 orig = S(uv);
float g = exp(-d * d * 16.0) * u_amount * 0.9;
c = 1.0 - (1.0 - orig) * (1.0 - vec3(1.0, 0.95, 0.8) * g);`,
    },
    {
        id: "light_leak_fx",
        name: "Light Leak",
        cat: "glow",
        controls: ["amount"],
        amountLabel: "Glow amount",
        amountDefault: 0.6,
        body: `float d = distance(uv, vec2(0.88, 0.1));
vec3 orig = S(uv);
float g = exp(-d * 5.5) * u_amount;
c = 1.0 - (1.0 - orig) * (1.0 - vec3(1.0, 0.45, 0.15) * g);`,
    },
    {
        id: "magic_sparks",
        name: "Magic Sparks",
        cat: "glow",
        premium: true,
        controls: ["amount", "speed"],
        amountLabel: "Glow amount",
        amountDefault: 0.6,
        body: `float t = u_time * u_speed;
vec2 g = uv * vec2(26.0, 40.0) + vec2(0.0, t * 3.0);
float n = noise(g);
float sp = step(0.982, n);
float tw = 0.5 + 0.5 * sin(t * 8.0 + n * 40.0);
vec3 spark = vec3(1.0, 0.85, 0.4) * sp * tw * u_amount * 1.8;
vec3 orig = S(uv);
c = 1.0 - (1.0 - orig) * (1.0 - clamp(spark, 0.0, 1.0));`,
    },
    {
        id: "flash_fx",
        name: "Flash",
        cat: "glow",
        controls: ["amount", "speed"],
        amountLabel: "Flash amount",
        amountDefault: 0.6,
        body: `float f = pow(max(0.0, sin(u_time * u_speed * 2.5)), 6.0) * u_amount;
c = mix(S(uv), vec3(1.0), f * 0.85);`,
    },

    // ---------- COLOR ----------
    {
        id: "hdr",
        name: "HDR",
        cat: "color",
        controls: [],
        body: `vec3 o = S(uv);
vec3 col = clamp((o - 0.5) * (1.0 + 0.45 * u_amount) + 0.5, 0.0, 1.0);
float l = lum(col);
c = clamp(mix(vec3(l), col, 1.35) * vec3(1.02), 0.0, 1.0);`,
    },
    {
        id: "hdr_2",
        name: "HDR 2",
        cat: "color",
        premium: true,
        controls: [],
        body: `vec3 o = S(uv);
vec3 col = clamp((o - 0.5) * (1.0 + 0.6 * u_amount) + 0.5, 0.0, 1.0);
float l = lum(col);
vec3 lifted = mix(vec3(0.04, 0.09, 0.18), col, smoothstep(0.0, 0.55, l));
c = clamp(mix(vec3(lum(lifted)), lifted, 1.5), 0.0, 1.0);`,
    },
    {
        id: "cinematic_fx",
        name: "Cinematic",
        cat: "color",
        controls: [],
        body: `vec3 o = S(uv);
float l = lum(o);
vec3 shadows = vec3(0.0, 0.15, 0.22);
vec3 highs = vec3(1.06, 0.86, 0.62);
vec3 col = o * mix(shadows * 2.2, vec3(1.0), smoothstep(0.0, 0.45, l));
col += max(l - 0.55, 0.0) * (highs - vec3(1.0));
c = clamp(col, 0.0, 1.0);`,
    },
    {
        id: "vintage_fx",
        name: "Vintage",
        cat: "color",
        controls: [],
        body: `vec3 o = S(uv);
vec3 col = vec3(lum(o)) * vec3(1.15, 0.95, 0.75);
col = mix(col, col * vec3(1.0, 0.85, 0.7), 0.5);
float d = distance(uv, vec2(0.5));
c = col * (1.0 - smoothstep(0.4, 0.85, d) * 0.45);`,
    },
    {
        id: "bw_fx",
        name: "Black & White",
        cat: "color",
        controls: [],
        body: `vec3 o = S(uv);
c = clamp((vec3(lum(o)) - 0.5) * (1.0 + 0.35 * u_amount) + 0.5, 0.0, 1.0);`,
    },
    {
        id: "luminance",
        name: "Luminance",
        cat: "color",
        controls: [],
        body: `vec3 o = S(uv);
float l = lum(o);
float boost = smoothstep(0.4, 1.0, l) * 0.55 * (0.4 + u_amount);
c = clamp(o + vec3(boost), 0.0, 1.0);`,
    },
    {
        id: "color_flicker",
        name: "Color Flicker",
        cat: "color",
        controls: ["speed", "amount"],
        amountLabel: "Flicker depth",
        amountDefault: 0.5,
        body: `float f = sin(u_time * u_speed * 6.0) * 0.5 + 0.5;
vec3 o = S(uv);
vec3 shifted = o * mix(vec3(1.0), vec3(1.18, 0.88, 1.06), f);
c = mix(o, shifted, u_amount);`,
    },
    {
        id: "negative",
        name: "Negative",
        cat: "color",
        controls: [],
        body: `c = 1.0 - S(uv);`,
    },

    // ---------- SHARPEN ----------
    {
        id: "smart_sharpen",
        name: "Smart Sharpen",
        cat: "sharpen",
        controls: ["amount"],
        amountLabel: "Sharpen amount",
        amountDefault: 0.5,
        body: `float o = 0.0016;
vec3 orig = S(uv);
vec3 blurc = (S(uv + vec2(o, 0.0)) + S(uv - vec2(o, 0.0)) + S(uv + vec2(0.0, o)) + S(uv - vec2(0.0, o))) / 4.0;
c = clamp(orig + (orig - blurc) * (1.5 + 2.5 * u_amount), 0.0, 1.0);`,
    },
    {
        id: "sharpen_edges",
        name: "Sharpen Edges",
        cat: "sharpen",
        controls: ["amount"],
        amountLabel: "Sharpen amount",
        amountDefault: 0.5,
        body: `float o = 0.002;
vec3 orig = S(uv);
vec3 blurc = (S(uv + vec2(o, 0.0)) + S(uv - vec2(o, 0.0)) + S(uv + vec2(0.0, o)) + S(uv - vec2(0.0, o))) / 4.0;
float e = clamp(length(orig - blurc) * 6.0, 0.0, 1.0);
c = clamp(orig + (orig - blurc) * e * (2.0 + 3.0 * u_amount), 0.0, 1.0);`,
    },
    {
        id: "detail_boost",
        name: "Detail Boost",
        cat: "sharpen",
        controls: ["amount"],
        amountLabel: "Detail amount",
        amountDefault: 0.5,
        body: `float o = 0.004;
vec3 orig = S(uv);
vec3 blurc = (S(uv + vec2(o, o)) + S(uv - vec2(o, o)) + S(uv + vec2(o, -o)) + S(uv - vec2(-o, o))) / 4.0;
c = clamp(orig + (orig - blurc) * (1.0 + 2.0 * u_amount), 0.0, 1.0);`,
    },
    {
        id: "ultra_sharp",
        name: "Ultra Sharp",
        cat: "sharpen",
        controls: ["amount"],
        amountLabel: "Sharpen amount",
        amountDefault: 0.5,
        body: `float o = 0.0013;
vec3 orig = S(uv);
vec3 s1 = S(uv + vec2(o, 0.0)) + S(uv - vec2(o, 0.0)) + S(uv + vec2(0.0, o)) + S(uv - vec2(0.0, o));
vec3 s2 = S(uv + vec2(o, o)) + S(uv - vec2(o, o)) + S(uv + vec2(o, -o)) + S(uv - vec2(-o, o));
vec3 blurc = (s1 * 1.5 + s2) / 10.0;
c = clamp(orig + (orig - blurc) * (2.0 + 4.0 * u_amount), 0.0, 1.0);`,
    },

    // ---------- MOTION ----------
    {
        id: "shake",
        name: "Shake",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Shake amount",
        amountDefault: 0.5,
        body: `float t = floor(u_time * 24.0);
vec2 off = vec2(hash(vec2(t, 1.0)) - 0.5, hash(vec2(t, 7.0)) - 0.5) * 0.06 * u_amount;
c = S(uv + off);`,
    },
    {
        id: "swing",
        name: "Swing",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Swing amount",
        amountDefault: 0.5,
        body: `vec2 off = vec2(sin(u_time * u_speed * 2.2) * 0.035 * u_amount, 0.0);
c = S(uv + off);`,
    },
    {
        id: "zoom_lens",
        name: "Zoom Lens",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Zoom amount",
        amountDefault: 0.5,
        body: `float z = 1.0 + (sin(u_time * u_speed * 2.0) * 0.5 + 0.5) * 0.18 * u_amount;
c = S((uv - vec2(0.5)) / z + vec2(0.5));`,
    },
    {
        id: "ripple_motion",
        name: "Ripple",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Ripple amount",
        amountDefault: 0.5,
        body: `float d = distance(uv, vec2(0.5));
vec2 dir = normalize(uv - vec2(0.5) + vec2(0.0001));
c = S(uv + dir * sin(d * 30.0 - u_time * u_speed * 6.0) * 0.009 * u_amount);`,
    },
    {
        id: "rotation_fx",
        name: "Rotation",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Spin speed",
        amountDefault: 0.5,
        body: `float a = u_time * u_speed * 0.5 * (0.3 + u_amount);
vec2 p = uv - vec2(0.5);
c = S(vec2(p.x * cos(a) - p.y * sin(a), p.x * sin(a) + p.y * cos(a)) + vec2(0.5));`,
    },
    {
        id: "bounce_fx",
        name: "Bounce",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Bounce height",
        amountDefault: 0.5,
        body: `float cyc = fract(u_time * u_speed * 0.8);
float b = abs(sin(cyc * 3.14159)) * 0.07 * u_amount;
c = S(uv + vec2(0.0, -b));`,
    },
    {
        id: "pendulum",
        name: "Pendulum",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Swing amount",
        amountDefault: 0.5,
        body: `float a = sin(u_time * u_speed * 1.6) * 0.14 * u_amount;
vec2 p = uv - vec2(0.5, 1.25);
vec2 pr = vec2(p.x * cos(a) - p.y * sin(a), p.x * sin(a) + p.y * cos(a));
c = S(pr + vec2(0.5, 1.25));`,
    },
    {
        id: "camera_shake",
        name: "Camera Shake",
        cat: "motion",
        controls: ["amount", "speed"],
        amountLabel: "Shake amount",
        amountDefault: 0.5,
        body: `float t = u_time * u_speed;
vec2 slow = vec2(noise(vec2(t * 1.2, 3.7)) - 0.5, noise(vec2(t * 1.2, 9.1)) - 0.5) * 0.03 * u_amount;
vec2 fast = vec2(noise(vec2(t * 6.0, 1.3)) - 0.5, noise(vec2(t * 6.0, 5.9)) - 0.5) * 0.007 * u_amount;
c = S(uv + slow + fast);`,
    },

    // ---------- DISTORTION ----------
    {
        id: "warp",
        name: "Warp",
        cat: "distort",
        controls: ["amount"],
        amountLabel: "Warp amount",
        amountDefault: 0.5,
        body: `vec2 p = uv - vec2(0.5);
vec2 warped = uv + p * dot(p, p) * 1.1 * u_amount;
c = S(warped);`,
    },
    {
        id: "wave",
        name: "Wave",
        cat: "distort",
        controls: ["amount", "speed"],
        amountLabel: "Wave amount",
        amountDefault: 0.5,
        body: `vec2 off = vec2(sin(uv.y * 12.0 + u_time * u_speed * 3.0) * 0.014 * u_amount, 0.0);
c = S(uv + off);`,
    },
    {
        id: "ripple_dist",
        name: "Ripple Rings",
        cat: "distort",
        controls: ["amount", "speed"],
        amountLabel: "Ripple amount",
        amountDefault: 0.5,
        body: `float d = distance(uv, vec2(0.5));
float ring = fract(d * 2.0 - u_time * u_speed * 0.8);
float push = (ring - 0.5) * 0.035 * u_amount * (1.0 - smoothstep(0.2, 0.9, ring));
vec2 dir = normalize(uv - vec2(0.5) + vec2(0.0001));
c = S(uv + dir * push);`,
    },
    {
        id: "fisheye",
        name: "Fisheye",
        cat: "distort",
        controls: ["amount"],
        amountLabel: "Lens strength",
        amountDefault: 0.5,
        body: `vec2 p = uv - vec2(0.5);
float d = dot(p, p);
c = S(p * (1.0 - 0.7 * u_amount * d) + vec2(0.5));`,
    },
    {
        id: "liquid",
        name: "Liquid",
        cat: "distort",
        controls: ["amount", "speed"],
        amountLabel: "Melt amount",
        amountDefault: 0.5,
        body: `float t = u_time * u_speed;
vec2 off = vec2(sin(uv.y * 8.0 + t * 1.5) + cos(uv.x * 7.0 - t), sin(uv.x * 9.0 - t * 1.2) + cos(uv.y * 6.0 + t)) * 0.009 * u_amount;
c = S(uv + off);`,
    },
    {
        id: "chromatic_distortion",
        name: "Chromatic Distortion",
        cat: "distort",
        premium: true,
        controls: ["amount"],
        amountLabel: "Distortion",
        amountDefault: 0.5,
        body: `vec2 p = uv - vec2(0.5);
float d = length(p);
vec2 dir = p / max(d, 0.0001);
float k = 0.035 * u_amount * d;
c = vec3(S(uv + dir * k).r, S(uv).g, S(uv - dir * k).b);`,
    },

    // ---------- TRANSITIONS ----------
    {
        id: "tr_fade",
        name: "Fade",
        cat: "transitions",
        controls: [],
        body: `c = S(uv) * smoothstep(0.0, 0.7, u_prog);`,
    },
    {
        id: "tr_dissolve",
        name: "Dissolve",
        cat: "transitions",
        controls: [],
        body: `vec3 o = S(uv);
float g = 1.0 - smoothstep(0.0, 0.92, u_prog);
float dith = step(1.0 - g * 1.05, hash(floor(uv * (u_res / 3.0))));
c = o * (1.0 - dith);`,
    },
    {
        id: "tr_blur",
        name: "Blur",
        cat: "transitions",
        controls: [],
        body: `float g = 1.0 - smoothstep(0.0, 0.9, u_prog);
float o2 = g * 0.02;
vec3 col = vec3(0.0);
for (int k = 0; k < 8; k++) {
  float f = float(k) / 7.0 - 0.5;
  col += S(uv + vec2(o2 * f, o2 * f));
}
c = col / 8.0;`,
    },
    {
        id: "tr_wipe",
        name: "Wipe",
        cat: "transitions",
        controls: [],
        body: `vec3 o = S(uv);
float edge = u_prog * 1.3;
float w = smoothstep(edge - 0.04, edge, (uv.x + uv.y) * 0.5);
c = o * (1.0 - w);`,
    },
    {
        id: "tr_zoom",
        name: "Zoom",
        cat: "transitions",
        controls: [],
        body: `float z = mix(2.2, 1.0, smoothstep(0.0, 0.85, u_prog));
vec2 p2 = (uv - vec2(0.5)) / z + vec2(0.5);
vec3 o = S(p2);
c = o * smoothstep(0.0, 0.7, u_prog);`,
    },
    {
        id: "tr_spin",
        name: "Spin",
        cat: "transitions",
        controls: [],
        body: `float v = smoothstep(0.0, 0.9, u_prog);
float a = (1.0 - v) * 3.14159;
float z = mix(0.25, 1.0, v);
vec2 p = (uv - vec2(0.5)) / z;
vec2 p2 = vec2(p.x * cos(a) - p.y * sin(a), p.x * sin(a) + p.y * cos(a));
c = S(p2 + vec2(0.5)) * smoothstep(0.0, 0.75, u_prog);`,
    },
    {
        id: "tr_slide",
        name: "Slide",
        cat: "transitions",
        controls: [],
        body: `vec2 off = vec2(1.0 - smoothstep(0.0, 0.8, u_prog), 0.0);
vec3 o = S(uv - off);
c = o * smoothstep(0.0, 0.6, u_prog);`,
    },
    {
        id: "tr_flash",
        name: "Flash",
        cat: "transitions",
        controls: ["amount"],
        amountLabel: "Flash strength",
        amountDefault: 0.8,
        body: `vec3 o = S(uv);
float f = 1.0 - smoothstep(0.0, 0.5, u_prog);
c = mix(o, vec3(1.0), f * f * u_amount);`,
    },
    {
        id: "tr_glitch",
        name: "Glitch Transition",
        cat: "transitions",
        controls: ["amount"],
        amountLabel: "Glitch strength",
        amountDefault: 0.7,
        body: `float g = 1.0 - smoothstep(0.0, 0.9, u_prog);
float t = floor(u_time * 20.0);
vec2 off = vec2((hash(vec2(floor(uv.y * 20.0), t)) - 0.5) * 0.28 * g * u_amount, 0.0);
vec3 o = S(uv + off);
vec3 blocks = vec3(hash(vec2(t, floor(uv.x * 10.0) + floor(uv.y * 10.0))));
c = mix(o, blocks, g * 0.25 * u_amount);`,
    },
    {
        id: "tr_film",
        name: "Film Transition",
        cat: "transitions",
        premium: true,
        controls: [],
        body: `float g = 1.0 - smoothstep(0.0, 0.85, u_prog);
vec3 o = S(uv + vec2((hash(vec2(floor(u_time * 16.0), 2.0)) - 0.5) * 0.02 * g, 0.0));
float fl = step(0.97, hash(vec2(floor(u_time * 24.0), 9.0))) * g;
float vig = 1.0 - 0.6 * g * (0.5 + distance(uv, vec2(0.5)));
vec3 sep = vec3(lum(o)) * vec3(1.1, 0.9, 0.7);
c = mix(o, sep, g * 0.7) * vig + vec3(fl);`,
    },

    // ---------- CREATIVE ----------
    {
        id: "neon_outline",
        name: "Neon Outline",
        cat: "creative",
        premium: true,
        controls: ["amount"],
        amountLabel: "Neon strength",
        amountDefault: 0.6,
        body: `float o = 0.003;
float lx = lum(S(uv + vec2(o, 0.0))) - lum(S(uv - vec2(o, 0.0)));
float ly = lum(S(uv + vec2(0.0, o))) - lum(S(uv - vec2(0.0, o)));
float e = clamp(length(vec2(lx, ly)) * 11.0, 0.0, 1.0);
vec3 neon = mix(vec3(1.0, 0.55, 0.1), vec3(0.1, 0.85, 1.0), uv.y);
c = vec3(lum(S(uv))) * 0.25 + neon * e * (0.6 + u_amount);`,
    },
    {
        id: "comic",
        name: "Comic",
        cat: "creative",
        premium: true,
        controls: ["amount"],
        amountLabel: "Posterize",
        amountDefault: 0.5,
        body: `float o = 0.0025;
vec3 orig = S(uv);
vec3 blurc = (S(uv + vec2(o, 0.0)) + S(uv - vec2(o, 0.0)) + S(uv + vec2(0.0, o)) + S(uv - vec2(0.0, o))) / 4.0;
float e = clamp(length(orig - blurc) * 7.0, 0.0, 1.0);
float levels = mix(7.0, 3.0, u_amount);
vec3 p = floor(orig * levels + 0.5) / levels;
c = mix(p, vec3(0.0), e);`,
    },
    {
        id: "sketch",
        name: "Sketch",
        cat: "creative",
        premium: true,
        controls: ["amount"],
        amountLabel: "Pencil strength",
        amountDefault: 0.5,
        body: `float o = 0.002;
float lx = lum(S(uv + vec2(o, 0.0))) - lum(S(uv - vec2(o, 0.0)));
float ly = lum(S(uv + vec2(0.0, o))) - lum(S(uv - vec2(0.0, o)));
float e = clamp(1.0 - length(vec2(lx, ly)) * (4.0 + 9.0 * u_amount), 0.0, 1.0);
c = vec3(e);`,
    },
    {
        id: "film_grain_fx",
        name: "Film Grain",
        cat: "creative",
        controls: ["amount"],
        amountLabel: "Grain amount",
        amountDefault: 0.5,
        body: `vec3 o = S(uv);
float n = hash(uv * u_res + fract(u_time) * 100.0) - 0.5;
c = o + vec3(n * (0.08 + 0.28 * u_amount));`,
    },
    {
        id: "retro_film",
        name: "Retro Film",
        cat: "creative",
        controls: ["amount"],
        amountLabel: "Age amount",
        amountDefault: 0.5,
        body: `vec3 o = S(uv);
float n = hash(uv * u_res + fract(u_time) * 100.0) - 0.5;
vec3 sep = vec3(lum(o)) * vec3(1.12, 0.94, 0.78);
float fl = 0.96 + 0.04 * sin(u_time * 11.0);
float d = distance(uv, vec2(0.5));
float vig = 1.0 - smoothstep(0.35, 0.9, d) * (0.35 + 0.35 * u_amount);
c = (sep + vec3(n * 0.15)) * fl * vig;`,
    },
    {
        id: "prism",
        name: "Prism",
        cat: "creative",
        premium: true,
        controls: ["amount"],
        amountLabel: "Prism strength",
        amountDefault: 0.5,
        body: `vec2 p = uv - vec2(0.5);
float d = length(p);
float a = atan(p.y, p.x) + 3.14159;
float k = 3.14159 * 0.5 * u_amount;
float r = S(vec2(cos(a + k), sin(a + k)) * d + vec2(0.5)).r;
float b = S(vec2(cos(a - k), sin(a - k)) * d + vec2(0.5)).b;
c = vec3(r, S(uv).g, b);`,
    },
    {
        id: "double_exposure",
        name: "Double Exposure",
        cat: "creative",
        premium: true,
        server: true,
        controls: [],
        body: `c = S(uv);`,
    },
    {
        id: "pixel_art",
        name: "Pixel Art",
        cat: "creative",
        premium: true,
        controls: ["amount"],
        amountLabel: "Pixel size",
        amountDefault: 0.5,
        body: `float px = mix(180.0, 36.0, u_amount);
vec2 p = floor(uv * px) / px;
vec3 o = S(p);
c = floor(o * 6.0 + 0.5) / 6.0;`,
    },
];

export const PREMIUM_ENABLED = false;

export function isEffectLocked(def) {
    return !!def.premium && !PREMIUM_ENABLED;
}
